import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CollectionRef } from '../domain/collections';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { ActionError, EmptyState, LoadingState, StateForError } from '../components/PageStates';
import { Button } from '@/components/ui/button';
import { BellOff } from 'lucide-react';

/**
 * `/settings/muted` — the collections I muted, which stay out of my feed.
 * Only I can see this list (015 FR-016, FR-018).
 */
export default function MutedCollectionsPage() {
  const { audience } = useRepositories();
  const mutes = useLoad<CollectionRef[]>(() => audience.myMutes(), 'mutes');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const unmute = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await audience.unmute(id);
      mutes.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  let body: React.ReactNode;
  if (mutes.status === 'loading') body = <LoadingState label="Loading muted collections" />;
  else if (mutes.status === 'error')
    body = <StateForError error={mutes.error} onRetry={mutes.reload} />;
  else if (mutes.data.length === 0)
    body = (
      <EmptyState
        icon={<BellOff className="size-8" />}
        title="No muted collections"
        description="Mute a collection you follow to keep it out of your feed."
      />
    );
  else
    body = (
      <ul aria-label="Muted collections" className="space-y-3">
        {mutes.data.map((c) => (
          <li key={c.id} className="flex items-center gap-4 rounded-3xl bg-card p-5">
            <Link
              to={`/collections/${c.id}`}
              className="min-w-0 flex-1 font-semibold text-foreground hover:text-primary"
            >
              {c.name}
            </Link>
            <Button
              variant="secondary"
              size="sm"
              className="rounded-full"
              aria-label={`Unmute ${c.name}`}
              disabled={busy !== null}
              onClick={() => unmute(c.id)}
            >
              Unmute
            </Button>
          </li>
        ))}
      </ul>
    );

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Muted collections</h1>
        <p className="mt-2 text-muted-foreground">
          These stay out of your feed. You can still open them. Only you see this list.
        </p>
      </div>
      {error !== null && <ActionError error={error} />}
      {body}
    </div>
  );
}
